import React, { useState, useEffect, useImperativeHandle, forwardRef } from 'react'
import styled from 'styled-components'
import { Button } from 'reactstrap'
import { FiFilePlus } from 'react-icons/fi'

import { ActionBar } from './action-bar'
import { SummaryRow } from './summary-row'
import { TitleRow } from './title-row'
import { InputRow } from './input-row'

import { CopyCostModal } from '../tab-cost-planning/copy-cost-modal'
import { ConfirmSaveForm } from '../../form'
import { FONTS } from '../../../../constants'

// const fallbackNull = (number) => (number === 'NaN' || number === Infinity || !number ? '0.00' : number)

export const ChangeRequestTab = forwardRef((props, ref) => {
  const root = document.getElementById('root')
  const [isEdit, setIsEdit] = useState(false)
  const [canEdit, setCanEdit] = useState(false)
  const [showCopyModal, setShowCopyModal] = useState({ show: false })
  const [showConfirmSave, setShowConfirmSave] = useState(false)
  const [changeRequests, setChangeRequests] = useState([])
  const {
    rSelected,
    projectByID,
    editProjectInformation,
    getProjectInformationByID,
  } = props

  const presale_snapshot = projectByID && projectByID.presale_snapshot ? projectByID.presale_snapshot.snapshot : null
  const isPresale = presale_snapshot && rSelected === 1
// const totalManDays = isPresale ? fallbackNull(presale_snapshot.$total_change_requests_man_days) : fallbackNull(projectByID.$total_change_requests_man_days)
// const totalPrice = isPresale ? fallbackNull(presale_snapshot.$total_change_requests_price) : fallbackNull(projectByID.$total_change_requests_price)

  useImperativeHandle(ref, () => ({
    getIsEdit() {
      return isEdit
    },
    resetIsEdit() {
      setIsEdit(false)
    },
  }))

  useEffect(() => {
    if (projectByID) setCanEdit(projectByID.$can_edit)
  }, [projectByID])

  useEffect(() => {
    if (projectByID && projectByID.change_requests) {
      if (!isPresale) {
        setChangeRequests([...projectByID.change_requests])
      } else if (presale_snapshot.change_requests) {
        setChangeRequests([...presale_snapshot.change_requests])
      }
    }
  }, [projectByID, rSelected])

  useEffect(() => {
    if (isEdit) {
      root.scrollTop = root.scrollHeight - root.clientHeight
    }
  }, [changeRequests.length])

  const editChangeRequest = () => {
    setShowConfirmSave(!showConfirmSave)
  }

  const handleClickAdd = () => {
    setChangeRequests(
      [
        ...changeRequests,
        {
          cr_type: 'SERVICE',
          qt_number: '',
          name: '',
          description: '',
          man_days: 0,
          cost: 0,
          price: 0,
        },
      ]
    )
  }

  const handleChangeInput = (e, id) => {
    const newChangeRequests = [...changeRequests]
    const changeRequest = { ...newChangeRequests[id] }
    const field = e.target.name
    if (field === 'price' || field === 'cost') {
      changeRequest[field] = parseFloat(e.target.value.replace(/,/g, ''))
    } else {
      changeRequest[field] = e.target.value
    }
    newChangeRequests[id] = changeRequest
    setChangeRequests(newChangeRequests)
  }

  const handleChangeSelect = (e, id, name) => {
    const newChangeRequests = [...changeRequests]
    const changeRequest = { ...newChangeRequests[id] }
    changeRequest[name] = e.value
    if (name === 'cr_type') {
      const canEditManDays = ['SERVICE', 'OTHER_SERVICE'].includes(e.value)
      const canEditCost = e.value !== 'SERVICE'
      const canEditDiliveryDateAndCreditTerm = e.value !== 'SERVICE'
      if (!canEditDiliveryDateAndCreditTerm) {
        delete changeRequest.delivery_date
        delete changeRequest.credit_term
      }
      changeRequest.man_days = !canEditManDays ? 0 : changeRequest.man_days
      changeRequest.cost = !canEditCost ? 0 : changeRequest.cost
    }
    newChangeRequests[id] = changeRequest
    setChangeRequests(newChangeRequests)
  }

  const handleChangeDate = (e, id) => {
    const newChangeRequests = [...changeRequests]
    const changeRequest = { ...newChangeRequests[id] }
    changeRequest.delivery_date = e || ''
    newChangeRequests[id] = changeRequest
    setChangeRequests(newChangeRequests)
  }

  const confirmEditChangeRequest = () => {
    const payload = { ...projectByID }
    delete payload.project_type
    delete payload.created
    delete payload.project_approve_status
    delete payload.created_by
    delete payload.project_number
    delete payload.__v
    delete payload.project_status
    delete payload._id
    delete payload.updated_by
    payload.change_requests = [...changeRequests]
    editProjectInformation(payload)
    setShowConfirmSave(false)
    setIsEdit(false)
  }

  const copyRow = (id) => {
    const _cr = { ...changeRequests[id] }
    delete _cr.cr_id
    const newChangeRequests = [...changeRequests, _cr]
    setChangeRequests(newChangeRequests)
  }

  const copyCost = () => {
    const numberOfCopies = document.getElementById('numberOfCopies')
    const id = showCopyModal.id
    const _cr = changeRequests[id]
    delete _cr.cr_id
    const newChangeRequests = [...changeRequests, ...Array(+numberOfCopies.value).fill(_cr)]
    setChangeRequests(newChangeRequests)
    setShowCopyModal({ show: false })
  }

  const deleteRow = (id) => {
    const newChangeRequests = [...changeRequests]
    newChangeRequests.splice(id, 1)
    setChangeRequests(newChangeRequests)
  }

  const handleClickEdit = () => {
    editChangeRequest()
  }

  const handleClickCancel = () => {
    setIsEdit(false)
    getProjectInformationByID()
  }

  return (
    <Root>
      <ConfirmSaveForm
        open={ showConfirmSave }
        toggle={ () => setShowConfirmSave(!showConfirmSave) }
        confirmSave={ confirmEditChangeRequest }
      />
      <CopyCostModal
        open={ showCopyModal.show }
        toggle={ setShowCopyModal }
        copyCost={ copyCost }
      />
      <ActionBar
        isEdit={ isEdit }
        canEdit={ canEdit }
        projectByID={ projectByID }
        isPresale={ isPresale }
        setIsEdit={ setIsEdit }
        clickEdit={ handleClickEdit }
        clickCancel={ handleClickCancel }
      />
      <SummaryRow
        projectByID={ projectByID }
      />
      <TitleRow />
      <RowWrapper>
        {changeRequests.map((changeRequest, key) => (
          <InputRow
            id={ key.toString() }
            key={ key }
            changeRequest={ changeRequest }
            isEdit={ isEdit }
            handleChangeInput={ handleChangeInput }
            handleChangeSelect={ handleChangeSelect }
            handleChangeDate={ handleChangeDate }
            setShowCopyModal={ setShowCopyModal }
            copyRow={ copyRow }
            deleteRow={ deleteRow }
          />
        ))}
      </RowWrapper>
      { isEdit &&
        <AddButton onClick={ handleClickAdd }>
          <FiFilePlus style={ { fontSize: 16 } } />
          <AddText>Add Change Request</AddText>
        </AddButton>
      }
    </Root>
  )
})

const Root = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
`

const RowWrapper = styled.div`
  height: 100%;
`

const AddButton = styled(Button)`
  width: 170px;
  height: 26px;
  display: flex;
  justify-content: center;
  align-items: center;
  background: #27AE60;
  border-radius: 4px;
  border: none;
  margin: 16px;
  padding: 0;

  svg {
    margin-right: 8px;
  }
`

const AddText = styled.span`
  font-family: ${FONTS.GOTHAM};
  font-size: ${FONTS.SMALL_SIZE};
  line-height: 180%;
`



// WEBPACK FOOTER //
// ./src/components/project-information/tab-content/tab-change-request/index.js